<template>
	<div class="container-fluid p-4">
		<div v-if="error" class="alert alert-danger" role="alert">{{ error }}</div>

		<div class="card mb-4">
			<div class="card-body">
				<h2 class="h5 mb-0">Mint labels</h2>
				<p class="text-muted mb-3 small">
					The server registers them now; the stickers are printed in advance.
				</p>
				<form class="row g-2 align-items-end" @submit.prevent="mint">
					<div class="col-12 col-md-3 col-lg-2">
						<label class="form-label" for="mintCount">Count</label>
						<input
							id="mintCount"
							v-model="mintCount"
							type="number"
							min="1"
							class="form-control"
							required
						/>
					</div>
					<div class="col-12 col-md-4 col-lg-3">
						<label class="form-label" for="mintBatch">Batch</label>
						<input
							id="mintBatch"
							v-model="mintBatch"
							type="text"
							class="form-control"
							placeholder="Optional batch name"
						/>
					</div>
					<div class="col-12">
						<ButtonLoading
							:loading="minting"
							class="btn btn-primary"
							type="submit"
						>
							Mint
						</ButtonLoading>
					</div>
				</form>
			</div>
		</div>

		<div class="card mb-4">
			<div class="card-body">
				<h2 class="h5 mb-0">Bind a label</h2>
				<p class="text-muted mb-3 small">
					A label is bound to one trap instance at a time; a label already open
					elsewhere is refused.
				</p>
				<form class="row g-2 align-items-end" @submit.prevent="bind">
					<div class="col-12 col-md-5 col-lg-4">
						<label class="form-label" for="bindLabel">Label</label>
						<input
							id="bindLabel"
							v-model="bindLabel"
							type="text"
							class="form-control"
							placeholder="Fallback (e.g. slug-ABCDEF) or token"
							required
						/>
					</div>
					<div class="col-12 col-md-3 col-lg-2">
						<label class="form-label" for="bindInstance"
							>Trap instance id</label
						>
						<input
							id="bindInstance"
							v-model="bindInstanceID"
							type="number"
							class="form-control"
							required
						/>
					</div>
					<div class="col-12">
						<ButtonLoading
							:loading="binding"
							class="btn btn-primary"
							type="submit"
						>
							Bind
						</ButtonLoading>
					</div>
				</form>
			</div>
		</div>

		<div class="card">
			<div class="card-body">
				<div class="d-flex justify-content-between align-items-center mb-3">
					<h2 class="h5 mb-0">The district's labels</h2>
					<button
						v-if="labels.length > 0"
						type="button"
						class="btn btn-outline-secondary btn-sm"
						@click="printSheet"
					>
						<i class="bi bi-printer me-1"></i> Print label sheet
					</button>
				</div>
				<div v-if="loading" class="text-muted">Loading…</div>
				<div v-else-if="labels.length === 0" class="text-muted">
					No labels minted yet.
				</div>
				<table v-else class="table align-middle mb-0">
					<thead>
						<tr>
							<th>Fallback</th>
							<th>QR</th>
							<th>State</th>
							<th>Binding</th>
							<th class="text-end">Release</th>
							<th class="text-end">Change state</th>
						</tr>
					</thead>
					<tbody>
						<template v-for="label in labels" :key="label.id">
							<tr>
								<td>
									<code>{{ label.label }}</code>
								</td>
								<td>
									<QrCode
										:src="`/api/qr-code/trap-label/${label.id}`"
										:alt="`${label.label} QR code`"
									/>
								</td>
								<td>
									<span class="badge text-bg-light">{{
										TRAP_LABEL_STATE_LABELS[label.state] ?? label.state
									}}</span>
								</td>
								<td>
									<span v-if="!label.open_binding" class="text-muted">
										Available
									</span>
									<span v-else>
										Instance
										<RouterLink
											:to="`/_/traps/${label.open_binding.instance_id}`"
										>
											#{{ label.open_binding.instance_id }}
										</RouterLink>
										<span class="badge ms-1 text-bg-info">
											{{
												label.open_binding.instance_collected
													? "at lab"
													: "in field"
											}}
										</span>
									</span>
								</td>
								<td class="text-end">
									<button
										v-if="label.open_binding"
										class="btn btn-sm btn-outline-secondary"
										@click="beginRelease(label)"
									>
										Release
									</button>
								</td>
								<td class="text-end" style="min-width: 200px">
									<select
										class="form-select form-select-sm d-inline-block w-auto"
										:value="label.state"
										@change="
											changeState(
												label,
												($event.target as HTMLSelectElement).value,
											)
										"
									>
										<option
											v-for="(text, state) in TRAP_LABEL_STATE_LABELS"
											:key="state"
											:value="state"
										>
											{{ text }}
										</option>
									</select>
								</td>
							</tr>
							<tr v-if="releasingID === label.id">
								<td colspan="6" class="bg-light">
									<form
										class="row g-2 align-items-end"
										@submit.prevent="release(label)"
									>
										<div class="col-12 col-md-3">
											<label class="form-label" for="releaseResolution"
												>Resolution</label
											>
											<select
												id="releaseResolution"
												v-model="releaseResolution"
												class="form-select form-select-sm"
											>
												<option value="manual_release">Manual release</option>
												<option value="label_lost">Label lost</option>
												<option value="label_damaged">Label damaged</option>
											</select>
										</div>
										<div class="col-12 col-md-5">
											<label class="form-label" for="releaseReason"
												>Reason</label
											>
											<input
												id="releaseReason"
												v-model="releaseReason"
												type="text"
												class="form-control form-control-sm"
												placeholder="Why is the binding being closed?"
												required
											/>
										</div>
										<div class="col-12">
											<ButtonLoading
												:loading="releasing"
												class="btn btn-sm btn-danger"
												type="submit"
											>
												Confirm release
											</ButtonLoading>
											<button
												type="button"
												class="btn btn-sm btn-outline-secondary ms-2"
												@click="cancelRelease"
											>
												Cancel
											</button>
										</div>
									</form>
								</td>
							</tr>
						</template>
					</tbody>
				</table>
			</div>
		</div>

		<!-- The label sheet, printed with the browser's own print: each label's QR
		     and human fallback, one sticker per cell. -->
		<PrintSheet>
			<h2 class="h5 text-center mb-4">Sample labels</h2>
			<div class="sticker-grid">
				<div v-for="label in labels" :key="label.id" class="sticker">
					<img
						:src="`/api/qr-code/trap-label/${label.id}`"
						:alt="`${label.label} QR code`"
						width="160"
						height="160"
					/>
					<div class="sticker-label">{{ label.label }}</div>
				</div>
			</div>
		</PrintSheet>

		<ToastNotification
			v-if="toastShow"
			:title="toastTitle"
			:message="toastMessage"
			:show="toastShow"
			@update:show="toastShow = $event"
		/>
	</div>
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue";
import { RouterLink } from "vue-router";

import { apiClient } from "@/client";
import ButtonLoading from "@/components/common/ButtonLoading.vue";
import PrintSheet from "@/components/common/PrintSheet.vue";
import ToastNotification from "@/components/ToastNotification.vue";
import QrCode from "@/components/traps/QrCode.vue";
import { TRAP_LABEL_STATE_LABELS } from "@/lib/trapState";
import type {
	TrapLabelBindingOpenRequest,
	TrapLabelBindingView,
	TrapLabelMintRequest,
	TrapLabelView,
} from "@/type/api";

const toastTitle = ref("");
const toastMessage = ref("");
const toastShow = ref(false);

function showNotification(title: string, message: string) {
	toastTitle.value = title;
	toastMessage.value = message;
	toastShow.value = true;
}

const labels = ref<TrapLabelView[]>([]);
const loading = ref(false);
const minting = ref(false);
const binding = ref(false);
const releasing = ref(false);
const error = ref("");

const mintCount = ref(1);
const mintBatch = ref("");
const bindLabel = ref("");
const bindInstanceID = ref<number | null>(null);

const releasingID = ref<number | null>(null);
const releaseResolution = ref("manual_release");
const releaseReason = ref("");

async function load() {
	loading.value = true;
	error.value = "";
	try {
		labels.value = await apiClient.JSONGet<TrapLabelView[]>("/api/trap-labels");
	} catch (e: unknown) {
		error.value = e instanceof Error ? e.message : "Failed to load labels.";
	} finally {
		loading.value = false;
	}
}

async function mint() {
	minting.value = true;
	error.value = "";
	const body: TrapLabelMintRequest = {
		count: mintCount.value,
		batch: mintBatch.value || null,
	};
	try {
		const minted = await apiClient.JSONPost<TrapLabelView[]>(
			"/api/trap-labels",
			body,
		);
		showNotification("Labels minted", `${minted.length} label(s) registered.`);
		mintBatch.value = "";
		await load();
	} catch (e: unknown) {
		error.value = e instanceof Error ? e.message : "Failed to mint labels.";
	} finally {
		minting.value = false;
	}
}

async function bind() {
	binding.value = true;
	error.value = "";
	const body: TrapLabelBindingOpenRequest = {
		label: bindLabel.value,
		instance_id: bindInstanceID.value ?? 0,
	};
	try {
		const opened = await apiClient.JSONPost<TrapLabelBindingView>(
			"/api/trap-label-binding",
			body,
		);
		showNotification(
			"Label bound",
			`Binding ${opened.id} is open on instance ${opened.trap_instance_id}.`,
		);
		bindLabel.value = "";
		bindInstanceID.value = null;
		await load();
	} catch (e: unknown) {
		error.value = e instanceof Error ? e.message : "Failed to bind the label.";
	} finally {
		binding.value = false;
	}
}

function beginRelease(label: TrapLabelView) {
	releasingID.value = label.id;
	releaseResolution.value = "manual_release";
	releaseReason.value = "";
}

function cancelRelease() {
	releasingID.value = null;
}

async function release(label: TrapLabelView) {
	if (!label.open_binding) return;
	releasing.value = true;
	error.value = "";
	try {
		await apiClient.JSONPost(
			`/api/trap-label-binding/${label.open_binding.binding_id}/release`,
			{ resolution: releaseResolution.value, reason: releaseReason.value },
		);
		showNotification("Label released", `${label.label} is free again.`);
		cancelRelease();
		await load();
	} catch (e: unknown) {
		error.value =
			e instanceof Error ? e.message : "Failed to release the label.";
	} finally {
		releasing.value = false;
	}
}

function printSheet() {
	window.print();
}

async function changeState(label: TrapLabelView, state: string) {
	if (state === label.state) return;
	error.value = "";
	try {
		await apiClient.JSONPatch(`/api/trap-label/${label.id}`, { state });
		showNotification(
			"State changed",
			`${label.label} is now ${TRAP_LABEL_STATE_LABELS[state] ?? state}.`,
		);
		await load();
	} catch (e: unknown) {
		error.value =
			e instanceof Error ? e.message : "Failed to change the label's state.";
	}
}

onMounted(load);
</script>

<style scoped>
.sticker-grid {
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	gap: 1rem;
}
.sticker {
	border: 1px dashed #adb5bd;
	border-radius: 0.5rem;
	padding: 1rem;
	text-align: center;
	break-inside: avoid;
}
.sticker-label {
	font-weight: 600;
	margin-top: 0.5rem;
}
</style>
